import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import EmployeeList from './EmployeeList';
import Departments from './Departments';
import Designations from './Designations';
import Locations from './Locations';
import CompanyInfo from './CompanyInfo';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const CompanyConfig = () => {
  const { user, userProfile } = useAuth();
  const { isCollapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const getTabFromPath = (path) => {
    if (path.includes('employee-list')) return 'employee-list';
    if (path.includes('departments')) return 'departments';
    if (path.includes('designations')) return 'designations';
    if (path.includes('locations')) return 'locations';
    if (path.includes('company-info')) return 'company-info';
    return 'employee-list';
  };

  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  React.useEffect(() => {
    const newTab = getTabFromPath(location.pathname);
    setActiveTab(newTab);
  }, [location.pathname]);

  const tabs = [
    { id: 'employee-list', label: 'Employee List', icon: 'Users', path: '/erp/company-config/employee-list' },
    { id: 'departments', label: 'Departments', icon: 'Building', path: '/erp/company-config/departments' },
    { id: 'designations', label: 'Designations', icon: 'Award', path: '/erp/company-config/designations' },
    { id: 'locations', label: 'Locations', icon: 'MapPin', path: '/erp/company-config/locations' },
    { id: 'company-info', label: 'Company Info', icon: 'Building2', path: '/erp/company-config/company-info' }
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab.id);
    navigate(tab.path);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'employee-list':
        return <EmployeeList />;
      case 'departments':
        return <Departments />;
      case 'designations':
        return <Designations />;
      case 'locations':
        return <Locations />;
      case 'company-info':
        return <CompanyInfo />;
      default:
        return <EmployeeList />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header Navigation */}
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        user={user}
        userProfile={userProfile}
      />

      {/* Main Sidebar */}
      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content */}
      <div className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          {/* Breadcrumb */}
          <BreadcrumbNavigation />

          <div className="border-b border-border bg-background -mx-6 px-6 mb-6">
            <div className="pt-6">
              <h1 className="text-3xl font-bold text-foreground mb-2">Company Configuration</h1>
              <p className="text-muted-foreground mb-4">Manage your company structure and information</p>
            </div>
            <div className="flex space-x-1 overflow-x-auto pb-4">
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? 'default' : 'ghost'}
                  onClick={() => handleTabChange(tab)}
                  className="flex items-center gap-2 whitespace-nowrap"
                >
                  <Icon name={tab.icon} size={16} />
                  {tab.label}
                </Button>
              ))}
            </div>
          </div>

          <div>{renderContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default CompanyConfig;

